import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { CaretDown, Question } from "@phosphor-icons/react";

const FAQ_ITEMS = [
  {
    cat: "Le service",
    items: [
      {
        q: "Doudou Alert, c'est quoi exactement ?",
        a: "Doudou Alert est une plateforme d'entraide qui aide les familles à retrouver les doudous perdus de leurs enfants. Vous créez une fiche pour chaque doudou (nom, photo, description), et si vous le perdez, les personnes qui le trouvent peuvent vous contacter en toute confiance et anonymement.",
      },
      {
        q: "Le service est-il vraiment gratuit ?",
        a: "Oui, l'inscription, la création de fiches doudou, la déclaration de perte et le chat avec les trouveurs sont 100 % gratuits.",
      },
      {
        q: "Pour qui est-ce conçu ?",
        a: "Pour toutes les familles avec enfants qui ont un doudou auquel ils tiennent (et qui ont peur de le perdre). Doudou Alert s'adresse aux parents, grands-parents, assistantes maternelles, crèches…",
      },
    ],
  },
  {
    cat: "Créer un compte",
    items: [
      {
        q: "Comment créer un compte ?",
        a: "Cliquez sur « Créer un compte » en haut à droite. Deux options : par email + mot de passe, ou directement avec votre compte Google (en un clic).",
      },
      {
        q: "Mes informations sont-elles protégées ?",
        a: "Oui. Vos mots de passe sont chiffrés avec bcrypt, les communications sont en HTTPS, et nous ne partageons aucune donnée avec des tiers. Voir notre politique de confidentialité pour plus de détails.",
      },
      {
        q: "Puis-je supprimer mon compte ?",
        a: "Oui, à tout moment depuis votre page Profil, ou en nous écrivant à contact@doudou-alert.fr. Toutes vos données sont alors effacées sous 30 jours conformément au RGPD.",
      },
    ],
  },
  {
    cat: "Fiches doudou",
    items: [
      {
        q: "Combien de doudous puis-je enregistrer ?",
        a: "Autant que vous voulez ! Il n'y a aucune limite, c'est gratuit.",
      },
      {
        q: "Dois-je mettre une photo ?",
        a: "Ce n'est pas obligatoire mais fortement recommandé : une photo augmente énormément les chances qu'on identifie votre doudou s'il est retrouvé.",
      },
      {
        q: "Que se passe-t-il quand je déclare un doudou perdu ?",
        a: "La fiche change de statut et vous pouvez indiquer la dernière position connue sur une carte interactive. Les familles peuvent ensuite voir votre signalement dans la rubrique « Carte des doudous signalés » si quelqu'un retrouve un doudou ressemblant.",
      },
    ],
  },
  {
    cat: "Quelqu'un a trouvé un doudou",
    items: [
      {
        q: "Faut-il créer un compte pour signaler un doudou trouvé ?",
        a: "Non, c'est volontairement sans inscription. Cliquez sur « J'ai trouvé un doudou » en haut du site. Vous laissez juste votre prénom et un message.",
      },
      {
        q: "Comment se passe le chat avec la famille ?",
        a: "Seul votre prénom est visible (pas votre nom, email ou téléphone). La conversation est privée entre vous deux. Vous pouvez convenir d'un rendez-vous ou choisir un point relais pour l'envoi.",
      },
      {
        q: "Comment faire si je ne peux pas rencontrer la famille ?",
        a: "Vous pouvez sélectionner ensemble un point relais. Le doudou voyage entre vous deux via le réseau de relais, sans que vos adresses ne soient révélées.",
      },
    ],
  },
  {
    cat: "Confidentialité & sécurité",
    items: [
      {
        q: "Mon adresse va-t-elle être révélée ?",
        a: "Jamais. Le chat n'affiche que les prénoms. Pour l'envoi du doudou, le système de points relais garantit l'anonymat des deux côtés.",
      },
      {
        q: "Mes photos vont-elles être visibles publiquement ?",
        a: "La photo de votre doudou est visible uniquement par les autres familles inscrites, dans la rubrique « Carte des doudous signalés ». Aucune photo de vous ou de votre enfant n'est demandée.",
      },
      {
        q: "Que faites-vous des données de localisation ?",
        a: "Les coordonnées GPS d'une perte ou d'un signalement sont stockées uniquement pour afficher le point sur une carte. Elles ne sont jamais croisées avec votre identité ni revendues.",
      },
    ],
  },
];

function Item({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      onClick={() => setOpen(!open)}
      className="w-full text-left bg-white rounded-2xl border border-rose-100 p-5 hover:shadow-md transition"
      data-testid="faq-item"
    >
      <div className="flex items-start gap-3">
        <span className="text-orange-500 mt-1"><Question size={20} weight="duotone" /></span>
        <div className="flex-1">
          <h3 className="font-display font-bold text-stone-800">{q}</h3>
          {open && <p className="text-stone-600 mt-3 leading-relaxed">{a}</p>}
        </div>
        <CaretDown size={18} weight="bold" className={`text-stone-400 mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
    </button>
  );
}

export default function FAQ() {
  // Inject FAQPage JSON-LD so Google can display our Q/A as rich snippets.
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-jsonld";
    const flat = FAQ_ITEMS.flatMap((c) => c.items);
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: flat.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById("faq-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <Layout>
      <SEO title="Questions fréquentes (FAQ)" description="Toutes les réponses aux questions sur Doudou Alert : compte, fiches doudou, chat anonyme, points relais, sécurité des données." />
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="faq-page">
        <div className="text-center mb-10">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">Aide</span>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-stone-800 mt-2" data-testid="faq-title">
            Questions fréquentes
          </h1>
          <p className="text-stone-600 mt-2">Tout ce que vous voulez savoir sur Doudou Alert.</p>
        </div>

        {FAQ_ITEMS.map((cat) => (
          <section key={cat.cat} className="mb-10">
            <h2 className="font-display text-xl font-bold text-orange-600 mb-4">{cat.cat}</h2>
            <div className="space-y-3">
              {cat.items.map((it) => <Item key={it.q} q={it.q} a={it.a} />)}
            </div>
          </section>
        ))}

        <div className="mt-12 bg-gradient-to-br from-rose-500 to-orange-500 rounded-3xl p-8 text-white text-center shadow-xl shadow-rose-300/40">
          <p className="font-display text-2xl font-black">Vous ne trouvez pas votre réponse ?</p>
          <p className="text-white/90 mt-2">Écrivez-nous, on répond avec plaisir.</p>
          <a href="mailto:contact@doudou-alert.fr" data-testid="faq-contact-link">
            <button className="mt-6 bg-white text-rose-600 hover:bg-stone-50 rounded-full px-8 py-3 font-bold transition">
              contact@doudou-alert.fr
            </button>
          </a>
        </div>
      </div>
    </Layout>
  );
}
